import { useRef, useEffect } from "react";
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, Quote, Eraser } from "lucide-react";

/**
 * Minimal contenteditable rich-text editor.
 * Emits HTML on every input. Toolbar uses document.execCommand (works cross-browser).
 * Output is sanitized again on the backend with bleach.
 */
export const RichTextEditor = ({ value = "", onChange, placeholder = "Write your message…", testid = "rich-editor" }) => {
  const ref = useRef(null);

  // Keep the editor's innerHTML in sync only when the prop is explicitly reset (e.g., after send)
  useEffect(() => {
    if (ref.current && (value === "" || value === null) && ref.current.innerHTML !== "") {
      ref.current.innerHTML = "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    if (ref.current) {
      ref.current.focus();
      onChange?.(ref.current.innerHTML);
    }
  };

  const handleLink = () => {
    const url = window.prompt("Enter URL (https://…)");
    if (!url) return;
    const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    exec("createLink", safe);
  };

  const handleInput = () => {
    if (ref.current) onChange?.(ref.current.innerHTML);
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden focus-within:border-[#1E3A8A]" data-testid={`${testid}-wrapper`}>
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <ToolBtn onClick={() => exec("bold")} icon={Bold} testid={`${testid}-bold`} label="Bold" />
        <ToolBtn onClick={() => exec("italic")} icon={Italic} testid={`${testid}-italic`} label="Italic" />
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <ToolBtn onClick={() => exec("insertUnorderedList")} icon={List} testid={`${testid}-ul`} label="Bullets" />
        <ToolBtn onClick={() => exec("insertOrderedList")} icon={ListOrdered} testid={`${testid}-ol`} label="Numbered" />
        <ToolBtn onClick={() => exec("formatBlock", "blockquote")} icon={Quote} testid={`${testid}-quote`} label="Quote" />
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <ToolBtn onClick={handleLink} icon={LinkIcon} testid={`${testid}-link`} label="Link" />
        <ToolBtn onClick={() => exec("removeFormat")} icon={Eraser} testid={`${testid}-clear`} label="Clear formatting" />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-testid={`${testid}-area`}
        className="prose prose-sm max-w-none min-h-[140px] px-4 py-3 text-slate-800 outline-none [&_a]:text-[#1E3A8A] [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-[#FFD507] [&_blockquote]:pl-3 [&_blockquote]:text-slate-600 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        data-placeholder={placeholder}
        style={{ caretColor: "#1E3A8A" }}
      />
      <style>{`[contenteditable=true]:empty::before{content: attr(data-placeholder); color:#94a3b8; pointer-events:none;}`}</style>
    </div>
  );
};

const ToolBtn = ({ onClick, icon: Icon, testid, label }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={label}
    data-testid={testid}
    className="p-1.5 rounded hover:bg-white hover:shadow-sm text-slate-600 hover:text-[#1E3A8A] transition"
  >
    <Icon className="w-4 h-4" />
  </button>
);

export default RichTextEditor;
